/**
 * Currency formatting utility functions
 * Handles different number separators for various countries and currencies
 */

export interface CurrencyInfo {
  code: string;
  symbol: string;
  name: string;
}

/**
 * Number formatting systems by country/region
 * Maps currency codes to their respective number formatting systems
 */
const NUMBER_FORMATTING_SYSTEMS: { [key: string]: 'indian' | 'western' | 'chinese' | 'japanese' | 'arabic' | 'thai' } = {
  // Indian Subcontinent (Indian Number System)
  'INR': 'indian', // India
  'PKR': 'indian', // Pakistan
  'BDT': 'indian', // Bangladesh
  'LKR': 'indian', // Sri Lanka
  'NPR': 'indian', // Nepal
  'BTN': 'indian', // Bhutan
  'MVR': 'indian', // Maldives
  'MMK': 'indian', // Myanmar
  
  // Chinese Number System
  'CNY': 'chinese', // China
  'CNH': 'chinese', // Chinese Yuan Offshore
  'HKD': 'chinese', // Hong Kong
  'TWD': 'chinese', // Taiwan
  'MOP': 'chinese', // Macau
  
  // Japanese Number System
  'JPY': 'japanese', // Japan
  
  // Thai Number System
  'THB': 'thai', // Thailand
  
  // Arabic Number System (some variations)
  'AED': 'arabic', // UAE
  'SAR': 'arabic', // Saudi Arabia
  'QAR': 'arabic', // Qatar
  'KWD': 'arabic', // Kuwait
  'BHD': 'arabic', // Bahrain
  'OMR': 'arabic', // Oman
  'JOD': 'arabic', // Jordan
  'LBP': 'arabic', // Lebanon
  'EGP': 'arabic', // Egypt
  'MAD': 'arabic', // Morocco
  'TND': 'arabic', // Tunisia
  'DZD': 'arabic', // Algeria
  'LYD': 'arabic', // Libya
  'SDG': 'arabic', // Sudan
  'ETB': 'arabic', // Ethiopia
  'KES': 'arabic', // Kenya
  'UGX': 'arabic', // Uganda
  'TZS': 'arabic', // Tanzania
  'NGN': 'arabic', // Nigeria
  'GHS': 'arabic', // Ghana
  'ZAR': 'arabic', // South Africa
  'NAD': 'arabic', // Namibia
  'BWP': 'arabic', // Botswana
  'ZMW': 'arabic', // Zambia
  'MWK': 'arabic', // Malawi
  'MZN': 'arabic', // Mozambique
  'SZL': 'arabic', // Eswatini
  'LSL': 'arabic', // Lesotho
  'ZWL': 'arabic', // Zimbabwe
  'XOF': 'arabic', // West African CFA
  'XAF': 'arabic', // Central African CFA
  'XPF': 'arabic', // CFP Franc
  'MUR': 'arabic', // Mauritius
  'SCR': 'arabic', // Seychelles
  'SOS': 'arabic', // Somalia
  'DJF': 'arabic', // Djibouti
  'KMF': 'arabic', // Comoros
  'STD': 'arabic', // São Tomé and Príncipe
  'CVE': 'arabic', // Cape Verde
  'GMD': 'arabic', // Gambia
  'GNF': 'arabic', // Guinea
  'SLL': 'arabic', // Sierra Leone
  'LRD': 'arabic', // Liberia
  'GIP': 'arabic', // Gibraltar
  'FKP': 'arabic', // Falkland Islands
  'SHP': 'arabic', // Saint Helena
  'JEP': 'arabic', // Jersey
  'GGP': 'arabic', // Guernsey
  'IMP': 'arabic', // Isle of Man
  'ISK': 'arabic', // Iceland
  'FOK': 'arabic', // Faroe Islands
  'NIO': 'arabic', // Nicaragua
  'GTQ': 'arabic', // Guatemala
  'HNL': 'arabic', // Honduras
  'BZD': 'arabic', // Belize
  'BBD': 'arabic', // Barbados
  'TTD': 'arabic', // Trinidad and Tobago
  'XCD': 'arabic', // East Caribbean Dollar
  'ANG': 'arabic', // Netherlands Antillean Guilder
  'AWG': 'arabic', // Aruban Florin
  'SRD': 'arabic', // Suriname
  'GYD': 'arabic', // Guyana
  'VEF': 'arabic', // Venezuela
  'BOB': 'arabic', // Bolivia
  'PYG': 'arabic', // Paraguay
  'UZS': 'arabic', // Uzbekistan
  'TJS': 'arabic', // Tajikistan
  'KGS': 'arabic', // Kyrgyzstan
  'KZT': 'arabic', // Kazakhstan
  'TMT': 'arabic', // Turkmenistan
  'AZN': 'arabic', // Azerbaijan
  'GEL': 'arabic', // Georgia
  'AMD': 'arabic', // Armenia
  'BYN': 'arabic', // Belarus
  'MDL': 'arabic', // Moldova
  'UAH': 'arabic', // Ukraine
  'RSD': 'arabic', // Serbia
  'BAM': 'arabic', // Bosnia and Herzegovina
  'HRK': 'arabic', // Croatia
  'BGN': 'arabic', // Bulgaria
  'ALL': 'arabic', // Albania
  'MKD': 'arabic', // North Macedonia
  'RON': 'arabic', // Romania
  'XAU': 'arabic', // Gold
  'XAG': 'arabic', // Silver
  'XPT': 'arabic', // Platinum
  'XPD': 'arabic', // Palladium
  'BTC': 'arabic', // Bitcoin
  'ETH': 'arabic', // Ethereum
  'LTC': 'arabic', // Litecoin
  'XRP': 'arabic', // Ripple
  'ADA': 'arabic', // Cardano
  'DOT': 'arabic', // Polkadot
  'LINK': 'arabic', // Chainlink
  'UNI': 'arabic', // Uniswap
  'BCH': 'arabic', // Bitcoin Cash
  'XLM': 'arabic', // Stellar
  'VET': 'arabic', // VeChain
  'TRX': 'arabic', // TRON
  'EOS': 'arabic', // EOS
  'AAVE': 'arabic', // Aave
  'ALGO': 'arabic', // Algorand
  'ATOM': 'arabic', // Cosmos
  'AVAX': 'arabic', // Avalanche
  'AXS': 'arabic', // Axie Infinity
  'BAT': 'arabic', // Basic Attention Token
  'CAKE': 'arabic', // PancakeSwap
  'CHZ': 'arabic', // Chiliz
  'COMP': 'arabic', // Compound
  'CRV': 'arabic', // Curve
  'DASH': 'arabic', // Dash
  'DOGE': 'arabic', // Dogecoin
  'ENJ': 'arabic', // Enjin Coin
  'FIL': 'arabic', // Filecoin
  'FLOW': 'arabic', // Flow
  'FTM': 'arabic', // Fantom
  'GALA': 'arabic', // Gala
  'GRT': 'arabic', // The Graph
  'HBAR': 'arabic', // Hedera
  'HOT': 'arabic', // Holochain
  'ICP': 'arabic', // Internet Computer
  'KDA': 'arabic', // Kadena
  'KSM': 'arabic', // Kusama
  'MANA': 'arabic', // Decentraland
  'MATIC': 'arabic', // Polygon
  'MKR': 'arabic', // Maker
  'NEAR': 'arabic', // NEAR Protocol
  'NEO': 'arabic', // NEO
  'ONE': 'arabic', // Harmony
  'QTUM': 'arabic', // Qtum
  'RVN': 'arabic', // Ravencoin
  'SHIB': 'arabic', // Shiba Inu
  'SNX': 'arabic', // Synthetix
  'SOL': 'arabic', // Solana
  'SUSHI': 'arabic', // SushiSwap
  'THETA': 'arabic', // Theta
  'WAVES': 'arabic', // Waves
  'XEM': 'arabic', // NEM
  'XTZ': 'arabic', // Tezos
  'YFI': 'arabic', // Yearn.finance
  'ZEC': 'arabic', // Zcash
  'ZIL': 'arabic', // Zilliqa
  
  // Western Number System (default for most currencies)
  'USD': 'western', // United States
  'EUR': 'western', // European Union
  'GBP': 'western', // United Kingdom
  'CAD': 'western', // Canada
  'AUD': 'western', // Australia
  'NZD': 'western', // New Zealand
  'CHF': 'western', // Switzerland
  'SEK': 'western', // Sweden
  'NOK': 'western', // Norway
  'DKK': 'western', // Denmark
  'PLN': 'western', // Poland
  'CZK': 'western', // Czech Republic
  'HUF': 'western', // Hungary
  'RUB': 'western', // Russia
  'TRY': 'western', // Turkey
  'BRL': 'western', // Brazil
  'MXN': 'western', // Mexico
  'ARS': 'western', // Argentina
  'CLP': 'western', // Chile
  'COP': 'western', // Colombia
  'PEN': 'western', // Peru
  'UYU': 'western', // Uruguay
  'MYR': 'western', // Malaysia
  'SGD': 'western', // Singapore
  'KRW': 'western', // South Korea
  'PHP': 'western', // Philippines
  'IDR': 'western', // Indonesia
  'AFN': 'western', // Afghanistan
  'AOA': 'western', // Angola
  'BIF': 'western', // Burundi
  'BMD': 'western', // Bermuda
  'BND': 'western', // Brunei
  'BSD': 'western', // Bahamas
  'CDF': 'western', // Congo
  'CRC': 'western', // Costa Rica
  'CUC': 'western', // Cuba
  'CUP': 'western', // Cuba
  'DOP': 'western', // Dominican Republic
  'ERN': 'western', // Eritrea
  'FJD': 'western', // Fiji
  'HTG': 'western', // Haiti
  'ILS': 'western', // Israel
  'IQD': 'western', // Iraq
  'IRR': 'western', // Iran
  'JMD': 'western', // Jamaica
  'KHR': 'western', // Cambodia
  'KPW': 'western', // North Korea
  'LAK': 'western', // Laos
  'MGA': 'western', // Madagascar
  'MNT': 'western', // Mongolia
  'MRU': 'western', // Mauritania
  'RWF': 'western', // Rwanda
  'SBD': 'western', // Solomon Islands
  'SSP': 'western', // South Sudan
  'STN': 'western', // São Tomé and Príncipe (2020)
  'SVC': 'western', // El Salvador
  'SYP': 'western', // Syria
  'TOP': 'western', // Tonga
  'YER': 'western', // Yemen
};

/**
 * Format number with appropriate separators based on currency
 * @param amount - The amount to format
 * @param currencyCode - The currency code (e.g., 'INR', 'USD', 'EUR')
 * @returns Formatted string with appropriate separators
 */
export const formatCurrencyAmount = (amount: number | undefined, currencyCode: string): string => {
  if (!amount || isNaN(amount)) {
    return '0';
  }

  const formattingSystem = NUMBER_FORMATTING_SYSTEMS[currencyCode] || 'western';

  switch (formattingSystem) {
    case 'indian':
      return formatIndianNumber(amount);
    case 'chinese':
      return formatChineseNumber(amount);
    case 'japanese':
      return formatJapaneseNumber(amount);
    case 'thai':
      return formatThaiNumber(amount);
    case 'arabic':
      return formatArabicNumber(amount);
    case 'western':
    default:
      return formatWesternNumber(amount);
  }
};

/**
 * Format number using Indian number system (lakhs, crores)
 * @param amount - The amount to format
 * @returns Formatted string with Indian separators
 */
const formatIndianNumber = (amount: number): string => {
  const numStr = Math.abs(amount || 0).toString();
  
  // Handle numbers less than 1000
  if (numStr.length <= 3) {
    return numStr;
  }

  // Handle numbers with decimal places
  const parts = numStr.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas for Indian number system
  let formatted = '';
  const len = wholePart.length;
  
  // Add comma after first 3 digits from right, then every 2 digits
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 2 === 1 && i !== len - 1) {
      formatted += ',';
    }
    formatted += wholePart[i];
  }

  // Add decimal part if exists
  if (decimalPart) {
    formatted += '.' + decimalPart;
  }

  return formatted;
};

/**
 * Format number using Chinese number system
 * @param amount - The amount to format
 * @returns Formatted string with Chinese separators
 */
const formatChineseNumber = (amount: number): string => {
  const numStr = Math.abs(amount || 0).toString();
  
  // Handle numbers less than 10000
  if (numStr.length <= 4) {
    return numStr;
  }

  // Handle numbers with decimal places
  const parts = numStr.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas for Chinese number system (every 4 digits)
  let formatted = '';
  const len = wholePart.length;
  
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 4 === 0) {
      formatted += ',';
    }
    formatted += wholePart[i];
  }

  // Add decimal part if exists
  if (decimalPart) {
    formatted += '.' + decimalPart;
  }

  return formatted;
};

/**
 * Format number using Japanese number system
 * @param amount - The amount to format
 * @returns Formatted string with Japanese separators
 */
const formatJapaneseNumber = (amount: number): string => {
  const numStr = Math.abs(amount || 0).toString();
  
  // Handle numbers less than 10000
  if (numStr.length <= 4) {
    return numStr;
  }

  // Handle numbers with decimal places
  const parts = numStr.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas for Japanese number system (every 4 digits)
  let formatted = '';
  const len = wholePart.length;
  
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 4 === 0) {
      formatted += ',';
    }
    formatted += wholePart[i];
  }

  // Add decimal part if exists
  if (decimalPart) {
    formatted += '.' + decimalPart;
  }

  return formatted;
};

/**
 * Format number using Thai number system
 * @param amount - The amount to format
 * @returns Formatted string with Thai separators
 */
const formatThaiNumber = (amount: number): string => {
  const numStr = Math.abs(amount || 0).toString();
  
  // Handle numbers less than 1000
  if (numStr.length <= 3) {
    return numStr;
  }

  // Handle numbers with decimal places
  const parts = numStr.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas for Thai number system (similar to Indian but with different grouping)
  let formatted = '';
  const len = wholePart.length;
  
  // Add comma after first 3 digits from right, then every 3 digits
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 3 === 0) {
      formatted += ',';
    }
    formatted += wholePart[i];
  }

  // Add decimal part if exists
  if (decimalPart) {
    formatted += '.' + decimalPart;
  }

  return formatted;
};

/**
 * Format number using Arabic number system
 * @param amount - The amount to format
 * @returns Formatted string with Arabic separators
 */
const formatArabicNumber = (amount: number): string => {
  const numStr = Math.abs(amount || 0).toString();
  
  // Handle numbers less than 1000
  if (numStr.length <= 3) {
    return numStr;
  }

  // Handle numbers with decimal places
  const parts = numStr.split('.');
  const wholePart = parts[0];
  const decimalPart = parts[1] || '';

  // Add commas for Arabic number system (every 3 digits)
  let formatted = '';
  const len = wholePart.length;
  
  for (let i = 0; i < len; i++) {
    if (i > 0 && (len - i) % 3 === 0) {
      formatted += ',';
    }
    formatted += wholePart[i];
  }

  // Add decimal part if exists
  if (decimalPart) {
    formatted += '.' + decimalPart;
  }

  return formatted;
};

/**
 * Format number using Western number system (standard international)
 * @param amount - The amount to format
 * @returns Formatted string with Western separators
 */
const formatWesternNumber = (amount: number): string => {
  return (amount || 0).toLocaleString('en-US');
};

/**
 * Format currency with symbol and amount
 * @param amount - The amount to format
 * @param currency - Currency object with code and symbol
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number | undefined, currency: CurrencyInfo): string => {
  if (!amount || isNaN(amount)) {
    return `${currency.symbol}0`;
  }

  const formattedAmount = formatCurrencyAmount(amount, currency.code);
  return `${currency.symbol}${formattedAmount}`;
};

/**
 * Format currency range (min - max)
 * @param minAmount - Minimum amount
 * @param maxAmount - Maximum amount
 * @param currency - Currency object with code and symbol
 * @returns Formatted currency range string
 */
export const formatCurrencyRange = (
  minAmount: number | undefined, 
  maxAmount: number | undefined, 
  currency: CurrencyInfo
): string => {
  const formattedMin = formatCurrencyAmount(minAmount || 0, currency.code);
  const formattedMax = formatCurrencyAmount(maxAmount || 0, currency.code);
  return `${currency.symbol}${formattedMin} - ${currency.symbol}${formattedMax}`;
};

/**
 * Parse currency code from currency string (e.g., "Indian Rupee-INR" -> "INR")
 * @param currencyString - Currency string from the currency symbols array
 * @returns Currency code
 */
export const parseCurrencyCode = (currencyString: string): string => {
  const parts = currencyString.split('-');
  return parts[parts.length - 1] || 'INR';
};

/**
 * Get currency info from currency string
 * @param currencyString - Currency string from the currency symbols array
 * @returns Currency info object
 */
export const getCurrencyInfo = (currencyString: string): CurrencyInfo => {
  const parts = currencyString.split('-');
  const code = parts[parts.length - 1] || 'INR';
  const name = parts.slice(0, -1).join('-') || 'Indian Rupee';
  
  // Map currency codes to symbols
  const symbolMap: { [key: string]: string } = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'CAD': 'C$',
    'AUD': 'A$',
    'JPY': '¥',
    'CNY': '¥',
    'CHF': 'CHF',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'TRY': '₺',
    'BRL': 'R$',
    'MXN': '$',
    'ARS': '$',
    'CLP': '$',
    'COP': '$',
    'PEN': 'S/',
    'UYU': '$',
    'VND': '₫',
    'THB': '฿',
    'MYR': 'RM',
    'SGD': 'S$',
    'HKD': 'HK$',
    'TWD': 'NT$',
    'KRW': '₩',
    'PHP': '₱',
    'IDR': 'Rp',
    'PKR': '₨',
    'BDT': '৳',
    'LKR': 'Rs',
    'NPR': '₨',
    'MMK': 'K',
    'KHR': '៛',
    'LAK': '₭',
    'MNT': '₮',
    'AED': 'د.إ',
    'SAR': '﷼',
    'QAR': 'ر.ق',
    'KWD': 'د.ك',
    'BHD': '.د.ب',
    'OMR': 'ر.ع.',
    'JOD': 'د.ا',
    'LBP': 'ل.ل',
    'EGP': 'ج.م',
    'MAD': 'د.م.',
    'TND': 'د.ت',
    'DZD': 'د.ج',
    'LYD': 'ل.د',
    'SDG': 'ج.س.',
    'ETB': 'ብር',
    'KES': 'KSh',
    'UGX': 'USh',
    'TZS': 'TSh',
    'NGN': '₦',
    'GHS': 'GH₵',
    'ZAR': 'R',
    'NAD': 'N$',
    'BWP': 'P',
    'ZMW': 'ZK',
    'MWK': 'MK',
    'MZN': 'MT',
    'SZL': 'L',
    'LSL': 'L',
    'ZWL': 'Z$',
    'XOF': 'CFA',
    'XAF': 'FCFA',
    'XPF': 'CFP',
    'MUR': '₨',
    'SCR': '₨',
    'SOS': 'S',
    'DJF': 'Fdj',
    'KMF': 'CF',
    'STD': 'Db',
    'CVE': '$',
    'GMD': 'D',
    'GNF': 'FG',
    'SLL': 'Le',
    'LRD': 'L$',
    'GIP': '£',
    'FKP': '£',
    'SHP': '£',
    'JEP': '£',
    'GGP': '£',
    'IMP': '£',
    'ISK': 'kr',
    'FOK': 'kr',
    'NIO': 'C$',
    'GTQ': 'Q',
    'HNL': 'L',
    'BZD': 'BZ$',
    'BBD': 'Bds$',
    'TTD': 'TT$',
    'XCD': 'EC$',
    'ANG': 'ƒ',
    'AWG': 'ƒ',
    'SRD': '$',
    'GYD': 'G$',
    'VEF': 'Bs',
    'BOB': 'Bs',
    'PYG': '₲',
    'UZS': 'so\'m',
    'TJS': 'ЅM',
    'KGS': 'с',
    'KZT': '₸',
    'TMT': 'T',
    'AZN': '₼',
    'GEL': '₾',
    'AMD': '֏',
    'BYN': 'Br',
    'MDL': 'L',
    'UAH': '₴',
    'RSD': 'дин.',
    'BAM': 'KM',
    'HRK': 'kn',
    'BGN': 'лв',
    'ALL': 'L',
    'MKD': 'ден',
    'RON': 'lei',
    'XAU': 'XAU',
    'XAG': 'XAG',
    'XPT': 'XPT',
    'XPD': 'XPD',
    'BTC': '₿',
    'ETH': 'Ξ',
    'LTC': 'Ł',
    'XRP': 'XRP',
    'ADA': '₳',
    'DOT': 'DOT',
    'LINK': 'LINK',
    'UNI': 'UNI',
    'BCH': 'BCH',
    'XLM': 'XLM',
    'VET': 'VET',
    'TRX': 'TRX',
    'EOS': 'EOS',
    'AAVE': 'AAVE',
    'ALGO': 'ALGO',
    'ATOM': 'ATOM',
    'AVAX': 'AVAX',
    'AXS': 'AXS',
    'BAT': 'BAT',
    'CAKE': 'CAKE',
    'CHZ': 'CHZ',
    'COMP': 'COMP',
    'CRV': 'CRV',
    'DASH': 'DASH',
    'DOGE': 'DOGE',
    'ENJ': 'ENJ',
    'FIL': 'FIL',
    'FLOW': 'FLOW',
    'FTM': 'FTM',
    'GALA': 'GALA',
    'GRT': 'GRT',
    'HBAR': 'HBAR',
    'HOT': 'HOT',
    'ICP': 'ICP',
    'KDA': 'KDA',
    'KSM': 'KSM',
    'MANA': 'MANA',
    'MATIC': 'MATIC',
    'MKR': 'MKR',
    'NEAR': 'NEAR',
    'NEO': 'NEO',
    'ONE': 'ONE',
    'QTUM': 'QTUM',
    'RVN': 'RVN',
    'SHIB': 'SHIB',
    'SNX': 'SNX',
    'SOL': 'SOL',
    'SUSHI': 'SUSHI',
    'THETA': 'THETA',
    'WAVES': 'WAVES',
    'XEM': 'XEM',
    'XTZ': 'XTZ',
    'YFI': 'YFI',
    'ZEC': 'ZEC',
    'ZIL': 'ZIL'
  };

  return {
    code,
    name,
    symbol: symbolMap[code] || code
  };
}; 